namespace Client.Forms

open System
open Client.Infrastructure.Api
open Client.DomainTypes.Msg
open Elmish
open Fable.React
open Fulma
open Microsoft.FSharp.Core
open Shared.Dto.Dto
open Shared
open Shared.DtoTypes
open Shared.Infrastructure
open Client
open Thoth.Elmish


[<RequireQualifiedAccess>]
module ContactInfo =
    type Model = {
        Session: UserSession
        Name: string option
        Phone: string option
        PhoneErrorMessage: string option
        RequestRunning: bool
    }

    type FormResult =
        | Noop
        | CloseModal
        | CloseAndRefresh

    let private createNewModel session : Model = {
        Session = session
        Name = None
        Phone = None
        PhoneErrorMessage = None
        RequestRunning = false
    }

    let init session = createNewModel session

    let private createContactInfo userId name phone : AlertDto.NewContactInfo = {
        Name = name
        UserId = userId
        Data = AlertDto.Phone phone
    }

    let private characterValidForPhoneNumber character =
        Char.IsDigit character || character = '+'

    let private areAllCharactersValid (phone: string) : Result<string, string> =
        let charactersAreValid = List.forall characterValidForPhoneNumber (Seq.toList phone)

        if charactersAreValid then
            Result.Ok phone
        else
            Result.Error "Die Telefonnummer enthält ungültige Zeichen. Nur Zahlen und + sind erlaubt."

    let private phoneStartsWithPlusOrDoubleZeroes (phone: string) : Result<string, string> =
        let phoneStartsWithPlus = phone.StartsWith("+") || phone.StartsWith("00")

        if phoneStartsWithPlus then
            Result.Ok phone
        else
            Result.Error "Die Telefonnummer muss mit '00' oder '+' und der Ländervorwahl beginnen."

    let private validatePhoneNumber (maybePhone: string option) : Result<string, string> =
        match maybePhone with
        | Some phone ->
            phone
            |> Result.Ok
            |> Result.bind areAllCharactersValid
            |> Result.bind phoneStartsWithPlusOrDoubleZeroes
        | None -> Result.Error "Bitte geben Sie eine Telefonnummer ein."

    let createContactInfoMsg (userId: int) (model: Model) =
        match model.PhoneErrorMessage with
        | Some _ -> None
        | None ->
            Some createContactInfo
            |> Option.apply (Some userId)
            |> Option.apply model.Name
            |> Option.apply model.Phone
            |> Option.map ContactInfoFormMsg.CreateContactInfo

    let private phoneInput dispatch (model: Model) =
        Field.div [] [
            Label.label [] [ str "Telefonnummer" ]
            Control.div [] [
                Input.text [
                    match model.PhoneErrorMessage with
                    | Some _ -> Input.Color IsDanger
                    | None -> ()

                    Input.Placeholder "00431234567890"
                    model.Phone |> Option.defaultValue "" |> Input.Value
                    Input.OnChange(fun event ->
                        event.Value |> String.toOption |> ContactInfoFormMsg.PhoneChanged |> dispatch
                    )
                ]
            ]

            match model.PhoneErrorMessage with
            | Some message -> Help.help [ Help.Color IsDanger ] [ str message ]
            | None ->
                Help.help [] [
                    str "Bitte gib die Telefonnummer mit der Ländervorwahl ein. Beispiel: 0043664123456789"
                ]
        ]

    let form dispatch (model: Model) =
        form [] [
            Field.div [] [
                Label.label [] [ str "Name" ]
                Control.div [] [
                    Input.text [
                        model.Name |> Option.defaultValue "" |> Input.Value
                        Input.OnChange(fun event ->
                            event.Value |> String.toOption |> ContactInfoFormMsg.NameChanged |> dispatch
                        )
                    ]
                ]
            ]

            phoneInput dispatch model
        ]

    let saveButton dispatch (model: Model) =
        let maybeOnClick =
            createContactInfoMsg model.Session.User.Id model
            |> Option.map (fun msg -> Button.OnClick(fun _ -> dispatch msg))

        let buttonOptions = [
            Button.IsLoading model.RequestRunning
            Button.Color IsSuccess
            Button.Disabled(Option.isNone maybeOnClick)
        ]

        Button.button (List.addToListIfSome buttonOptions maybeOnClick) [ str "Speichern" ]


    let view (dispatch: ContactInfoFormMsg -> unit) (model: Model) =
        let closeModal = (fun _ -> dispatch ContactInfoFormMsg.CloseModal)

        let headline = "Neue Kontaktdaten anlegen"

        Modal.modal [ Modal.IsActive true ] [
            Modal.background [] []
            Modal.Card.card [] [
                Modal.Card.head [] [
                    Modal.Card.title [] [ str headline ]
                    Delete.delete [ Delete.OnClick closeModal ] []
                ]
                Modal.Card.body [] [ Content.content [] [ form dispatch model ] ]
                Modal.Card.foot [] [ saveButton dispatch model ]
            ]
        ]

    let update (msg: ContactInfoFormMsg) (model: Model) =
        match msg with
        | ContactInfoFormMsg.NameChanged name -> { model with Name = name }, Cmd.none, FormResult.Noop
        | PhoneChanged phone ->
            let validated = validatePhoneNumber phone

            let errorMessage =
                match validated with
                | Result.Ok _ -> None
                | Result.Error error -> Some error

            {
                model with
                    Phone = phone
                    PhoneErrorMessage = errorMessage
            },
            Cmd.none,
            FormResult.Noop
        | CreateContactInfo contactInfo ->
            let request = {
                SessionKey = model.Session.SessionKey
                Data = contactInfo
            }

            { model with RequestRunning = true },
            Cmd.OfAsync.perform api.createContactInfo request ContactInfoCreated,
            FormResult.Noop
        | ContactInfoCreated response ->
            match response with
            | Ok _ ->
                let toastCmd = Toast.create "Speichern erfolgreich" |> Toast.success

                model, toastCmd, FormResult.CloseAndRefresh
            | Error _ ->
                let toastCmd = Toast.create "Fehler beim Speichern" |> Toast.error

                model, toastCmd, FormResult.Noop
        | ContactInfoFormMsg.CloseModal -> model, Cmd.none, FormResult.CloseModal